'use client';

import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Link from 'next/link';

import { User, UserRolesEnum } from '@/app/api';
import { Country, Routes } from '@/app/enums';
import { PropsWithClassname } from '@/app/types';
import { getCountryConfig } from '@/app/utils/countryConfig';

import Button, { ButtonHTMLTag, ButtonStyle } from '@/app/components/Button/Button';
import EditIcon from '@/app/components/EditIcon/EditIcon';
import HeartCogIcon from '@/app/components/HeartCogIcon/HeartCogIcon';
import HeartOutlinedIcon from '@/app/components/HeartOutlinedIcon/HeartOutlinedIcon';
import HeartRefreshIcon from '@/app/components/HeartRefreshIcon/HeartRefreshIcon';
import ProfileNavigationHeader from '@/app/components/ProfileNavigationHeader/ProfileNavigationHeader';
import TeamIcon from '@/app/components/TeamIcon/TeamIcon';

interface ProfileMenuItem {
  title: string;
  path: string;
  icon: ReactNode;
  additionalContent?: ReactNode;
}

export interface ProfileNavigationProps extends PropsWithClassname {
  currentUser: User;
}

const countryConfig = getCountryConfig();

const ProfileNavigation = ({ currentUser, className }: ProfileNavigationProps) => {
  const { t } = useTranslation();

  const isAdminMenuVisible = useMemo(() => {
    return currentUser.roles?.includes(UserRolesEnum.Admin);
  }, [currentUser]);

  const menuItems: ProfileMenuItem[] = useMemo(() => {
    const storiesReported = {
      title: t('components.ProfileNavigation.storiesSupported'),
      path: Routes.MY_DONATIONS,
      icon: <HeartOutlinedIcon className="text-neutral-profileMenuIconsColor h-[24px] w-[24px]" />,
    };

    const regularDonor = {
      title: t('components.ProfileNavigation.regularDonor'),
      path: Routes.MY_DONIO_REGULAR_DONOR,
      icon: <HeartRefreshIcon className="text-neutral-profileMenuIconsColor h-[24px] w-[24px]" />,
    };

    const myChallenges = {
      title: t('components.ProfileNavigation.myChallenges'),
      path: Routes.MY_CHALLENGES,
      icon: <TeamIcon className="text-neutral-profileMenuIconsColor h-[24px] w-[24px]" />,
    };

    const myStories = {
      title: t('components.ProfileNavigation.myStories'),
      path: Routes.MY_STORIES,
      icon: <EditIcon className="text-neutral-profileMenuIconsColor h-[24px] w-[24px]" />,
      additionalContent: currentUser.projectsCount && (
        <span className="text-[12px] text-neutral-semiTransparent ml-[8px]">{currentUser.projectsCount}</span>
      ),
    };

    const czMenuItems = [storiesReported, regularDonor, myChallenges, myStories];
    const skMenuItems = [storiesReported, myChallenges, myStories];

    switch (countryConfig.country) {
      case Country.SLOVAKIA:
        return skMenuItems;
      case Country.CZECH_REPUBLIC:
      default:
        return czMenuItems;
    }
  }, [t]);

  const adminMenuItems: ProfileMenuItem[] = useMemo(() => {
    return [
      {
        title: t('components.ProfileNavigation.admin'),
        path: Routes.ADMIN,
        icon: <HeartCogIcon className="text-neutral-profileMenuIconsColor h-[24px] w-[24px]" />,
      },
    ];
  }, [t]);

  // TODO temporary workaround
  const menuItemsPrefixed = useMemo(() => {
    const baseUrl = process.env.NEXT_PUBLIC_REDIRECT_URL;

    return menuItems.map((item) => ({
      ...item,
      path: `${baseUrl}/${item.path}`,
    }));
  }, [menuItems]);

  // TODO temporary workaround
  const adminMenuItemsPrefixed = useMemo(() => {
    const baseUrl = process.env.NEXT_PUBLIC_REDIRECT_URL;

    return adminMenuItems.map((item) => ({
      ...item,
      path: `${baseUrl}/${item.path}`,
    }));
  }, [adminMenuItems]);

  return (
    <div className={className}>
      <ProfileNavigationHeader currentUser={currentUser} className="mb-[24px]" />
      <div>
        {isAdminMenuVisible && (
          <ul className="block w-full ml-[8px] mb-[8px]">
            {adminMenuItemsPrefixed.map((item) => (
              <li key={item.path} className="block w-full group">
                <Link
                  href={item.path}
                  prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
                  className="flex w-full hover:text-primary-mid items-center"
                >
                  {item.icon}
                  <span className="text-[16px] flex ml-[24px] py-[16px] border-b border-b-neutral-light grow group-last:border-b-0">
                    {item.title}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
        <span className="block text-[10px] md:text-[12px] mt-[8px] xl:mt-[0px] font-medium leading-[24px] uppercase tracking-[3px] md:tracking-[4.5px]">
          {t('components.ProfileNavigation.myDonio')}
        </span>
        <ul className="block w-full mb-[16px] ml-[8px]">
          {menuItemsPrefixed.map((item) => (
            <li key={item.path} className="block group">
              <Link
                href={item.path}
                prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
                className="flex w-full hover:text-primary-mid items-center justify-between"
              >
                {item.icon}
                <span className="text-[16px] flex justify-between px-[24px] py-[16px] border-b border-b-neutral-light grow group-last:border-b-0">
                  <span>{item.title}</span>
                  <span>{item.additionalContent}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <div className="flex flex-col">
          <Button
            tagSpecificProps={{
              htmlTag: ButtonHTMLTag.LINK,
              href: `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.SUGGESTION}`,
            }}
            className="text-center mb-[16px]"
          >
            {t('components.ProfileNavigation.startBrowsing')}
          </Button>
          <Button
            style={ButtonStyle.PRIMARY_OUTLINED}
            tagSpecificProps={{
              htmlTag: ButtonHTMLTag.LINK,
              href: `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.LOGOUT}`,
            }}
            className="text-center px-[16px] py-[10px] mb-[16px]"
          >
            {t('components.ProfileNavigation.signOut')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileNavigation;
